import type { Paginated } from '@helios/data-access';
import { apiClient, apiOrigin, type ApiResult, type RequestOptions } from '@/services/apiClient';

export type AdminRecordStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'DELETED';
export type CredentialDelivery = 'DISPLAY' | 'EMAIL';
export interface AdminUserRole { id: string; name: string; isSystem: boolean }
export interface AdminUserAllowedActions {
  editProfile: boolean; manageRoles: boolean; changeStatus: boolean; resetPassword: boolean;
  provisionAccess: boolean; revokeSessions: boolean; remove: boolean; restore: boolean;
}
export interface AdminUserItem {
  id: string; tenantId: string; tenantName: string; tenantStatus: AdminRecordStatus;
  email: string; name: string; title: string | null; status: AdminRecordStatus;
  version: number; authVersion: number; authenticationEnabled: boolean; mustChangePassword: boolean;
  temporaryPasswordExpiresAt: string | null; deletedAt: string | null;
  lastLoginAt: string | null; createdAt: string; roles: AdminUserRole[]; allowedActions: AdminUserAllowedActions;
}
export interface CreatedAdminUser extends AdminUserItem {
  temporaryPassword: string | null; credentialDelivery: CredentialDelivery; credentialEmailed: boolean;
  credentialEmailStatus: string; credentialEmailId: string | null;
  deliveryState: 'DISPLAYED' | 'ACCEPTED' | 'QUEUED' | 'SIMULATED' | 'FAILED' | 'UNKNOWN';
  sessionsRevoked: number; challengesConsumed: number; revokedTwinSessions: number;
}
export interface AdminUserHistoryItem {
  id: string; action: string; actorName: string | null; createdAt: string;
  before: unknown; after: unknown;
}
export interface AdminUserDetail extends AdminUserItem {
  effectivePermissions: string[]; passkeyCount: number; history: AdminUserHistoryItem[];
}
export interface AssignableAdminRole {
  id: string; tenantId: string | null; name: string; isSystem: boolean; permissionKeys: string[];
}
export interface AdminUserSessionRevocation extends AdminUserItem {
  sessionsRevoked: number; challengesConsumed: number; revokedTwinSessions: number;
}
export interface AdminSessionItem {
  id: string; tenantId: string; tenantName: string; userId: string; userName: string; userEmail: string;
  userAgent: string | null; ipAddress: string | null; createdAt: string; expiresAt: string;
  revokedAt: string | null; active: boolean; purpose: 'NORMAL' | 'PASSWORD_CHANGE'; authVersion: number;
  userVersion: number; canRevokeSessions: boolean;
}
export interface AdminPermissionItem {
  id: string; key: string; description: string | null; domain: string | null;
  resource: string | null; action: string | null;
}
export interface AdminRoleItem {
  id: string; tenantId: string | null; name: string; description: string | null;
  isSystem: boolean; version: number; userCount: number; permissionKeys: string[];
  /** Server-computed: this caller may edit the role's permissions. */
  canEdit?: boolean;
}
export interface AdminTenantItem {
  licensedUserLimit?: number | null;
  setupState?: string;
  id: string; name: string; slug: string; plan: string | null; status: string; version: number;
  userCount: number; projectCount: number; createdAt: string; updatedAt: string;
}
export interface AdminListQuery {
  page?: number; pageSize?: number; q?: string; tenantId?: string; userId?: string; status?: AdminRecordStatus;
}
export interface CreateAdminUserInput {
  email: string; name: string; title?: string; tenantId?: string; roleIds?: string[];
  credentialDelivery?: CredentialDelivery;
}
export interface UpdateAdminUserInput {
  expectedVersion: number; name?: string; title?: string;
  status?: Exclude<AdminRecordStatus, 'DELETED'>; roleIds?: string[];
}
export interface AdminUserActionInput { expectedVersion: number }
export interface AdminUserCredentialInput extends AdminUserActionInput { credentialDelivery: CredentialDelivery }
export interface ProvisionAdminUserInput extends AdminUserCredentialInput { roleIds: string[] }
export interface AdminUserLifecycleInput extends AdminUserActionInput { reason: string }
export interface CreateAdminRoleInput {
  name: string; description?: string; permissionKeys?: string[]; tenantId?: string;
}
export interface UpdateAdminTenantInput { expectedVersion: number; name?: string; status?: AdminRecordStatus; plan?: string | null; licensedUserLimit?: number; reviewReason?: string }

export class AdminRequestError extends Error {
  constructor(message: string, public readonly status: number, public readonly code: string | null) {
    super(message); this.name = 'AdminRequestError';
  }
}
function expectData<T>(result: ApiResult<T>): T {
  if (!result.ok || result.data === null) throw new AdminRequestError(result.error ?? 'API request failed', result.status, result.errorCode ?? null);
  return result.data;
}
function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') search.set(key, String(value));
  return search.size ? '?' + search.toString() : '';
}
const root = apiOrigin + '/api/v1/admin';
const userPath = (id: string) => root + '/users/' + encodeURIComponent(id);
function list<T>(path: string, query: AdminListQuery, defaultSize: number, options?: RequestOptions) {
  return apiClient.get<Paginated<T>>(root + '/' + path + queryString({ ...query, page: query.page ?? 1, pageSize: query.pageSize ?? defaultSize }), options).then(expectData);
}
export const adminClient = {
  listUsers: (query: AdminListQuery = {}, options?: RequestOptions) => list<AdminUserItem>('users', query, 25, options),
  getUser: (id: string, options?: RequestOptions) => apiClient.get<AdminUserDetail>(userPath(id), options).then(expectData),
  listUserHistory: (id: string, query: AdminListQuery = {}, options?: RequestOptions) => apiClient.get<Paginated<AdminUserHistoryItem>>(userPath(id) + '/history' + queryString({ page: query.page ?? 1, pageSize: query.pageSize ?? 25 }), options).then(expectData),
  listAssignableRoles: (query: AdminListQuery & { tenantId: string }, options?: RequestOptions) => list<AssignableAdminRole>('users/assignable-roles', query, 25, options),
  createUser: (input: CreateAdminUserInput, options?: RequestOptions) => apiClient.post<CreatedAdminUser>(root + '/users', input, options).then(expectData),
  updateUser: (id: string, input: UpdateAdminUserInput, options?: RequestOptions) => apiClient.patch<AdminUserItem>(userPath(id), input, options).then(expectData),
  resetPassword: (id: string, input: AdminUserCredentialInput, options?: RequestOptions) => apiClient.post<CreatedAdminUser>(userPath(id) + '/password-reset', input, options).then(expectData),
  provisionAccess: (id: string, input: ProvisionAdminUserInput, options?: RequestOptions) => apiClient.post<CreatedAdminUser>(userPath(id) + '/provision-access', input, options).then(expectData),
  revokeSessions: (id: string, input: AdminUserActionInput, options?: RequestOptions) => apiClient.post<AdminUserSessionRevocation>(userPath(id) + '/revoke-sessions', input, options).then(expectData),
  removeUser: (id: string, input: AdminUserLifecycleInput, options?: RequestOptions) => apiClient.delete<AdminUserItem>(userPath(id), options, input).then(expectData),
  restoreUser: (id: string, input: AdminUserLifecycleInput, options?: RequestOptions) => apiClient.post<AdminUserItem>(userPath(id) + '/restore', input, options).then(expectData),
  listSessions: (query: AdminListQuery = {}, options?: RequestOptions) => list<AdminSessionItem>('activity/sessions', query, 25, options),
  listRoles: (query: AdminListQuery = {}, options?: RequestOptions) => list<AdminRoleItem>('roles', query, 25, options),
  listPermissions: (options?: RequestOptions) => apiClient.get<AdminPermissionItem[]>(root + '/roles/permissions', options).then(expectData),
  createRole: (input: CreateAdminRoleInput, options?: RequestOptions) => apiClient.post<AdminRoleItem>(root + '/roles', input, options).then(expectData),
  updateRolePermissions: (id: string, permissionKeys: string[], expectedVersion: number, options?: RequestOptions) => apiClient.patch<AdminRoleItem>(root + '/roles/' + encodeURIComponent(id) + '/permissions', { permissionKeys, expectedVersion }, options).then(expectData),
  listTenants: (query: AdminListQuery = {}, options?: RequestOptions) => list<AdminTenantItem>('tenants', query, 25, options),
  updateTenant: (id: string, input: UpdateAdminTenantInput, options?: RequestOptions) => apiClient.patch<AdminTenantItem>(root + '/tenants/' + encodeURIComponent(id), input, options).then(expectData),
};
